import { Select } from "antd";
import { useTranslation } from "react-i18next";
import type { WorkingDay } from "../../../../api/configuration";
import { crossesMidnight } from "../../../../lib/workingHours";
import { SegmentedControl } from "../ordering-settings/SegmentedControl";
import { TIME_OPTIONS, type WorkingDayError } from "./workingHoursForm";

const TIME_SELECT_OPTIONS = TIME_OPTIONS.map((time) => ({ value: time, label: time }));

/**
 * Един ден от седмицата: вляво името и подсказката (затваря след полунощ /
 * почивен ден / грешката); вдясно „Работи | Почивен" и часовете „от – до".
 * Почивният ден пази часовете си (сиви), за да се върнат при „Работи".
 * На тесен контейнер двете колони се подреждат една под друга.
 */
export function WorkingDayRow({
  name,
  day,
  error,
  disabled,
  onChange,
}: {
  name: string;
  day: WorkingDay;
  error: WorkingDayError;
  disabled: boolean;
  onChange: (day: WorkingDay) => void;
}) {
  const { t } = useTranslation();

  const hint = error
    ? t(`workingHours.errors.${error}`)
    : day.closed
      ? t("workingHours.hintClosed")
      : crossesMidnight(day)
        ? t("workingHours.hintAfterMidnight", { time: day.closesAt })
        : null;

  return (
    <div className="grid gap-3 px-5 py-4 @2xl:grid-cols-[minmax(0,1fr)_auto] @2xl:items-center @2xl:gap-8">
      <div className="min-w-0">
        <div className="text-[13.5px] font-bold tracking-[-0.2px] text-ink">{name}</div>
        {hint && (
          <div
            role={error ? "alert" : undefined}
            className={`mt-1 text-[12.5px] leading-[1.45] ${error ? "font-semibold text-danger-ink" : "text-muted"}`}
          >
            {hint}
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <SegmentedControl
          options={[
            { value: false, label: t("workingHours.open") },
            { value: true, label: t("workingHours.closed") },
          ]}
          value={day.closed}
          disabled={disabled}
          onChange={(closed) => onChange({ ...day, closed })}
        />
        <div className="flex items-center gap-2">
          <Select
            aria-label={t("workingHours.opensAt")}
            value={day.opensAt}
            options={TIME_SELECT_OPTIONS}
            disabled={disabled || day.closed}
            onChange={(opensAt) => onChange({ ...day, opensAt })}
            className="!w-[96px]"
          />
          <span className="text-[13px] font-semibold text-muted">–</span>
          <Select
            aria-label={t("workingHours.closesAt")}
            value={day.closesAt}
            options={TIME_SELECT_OPTIONS}
            disabled={disabled || day.closed}
            onChange={(closesAt) => onChange({ ...day, closesAt })}
            className="!w-[96px]"
          />
        </div>
      </div>
    </div>
  );
}
